import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon, GripVerticalIcon } from 'lucide-react';
interface ChartItem {
  id: string;
  title: string;
  value?: string;
  subtext?: string;
}
interface EditChartsModalProps {
  onClose: () => void;
  onSave: (chartOrder: string[], visibleCharts: Set<string>) => void;
  initialChartOrder: string[];
  initialVisibleCharts: Set<string>;
  chartData: Map<string, ChartItem>;
  renderChart: (chartId: string) => React.ReactNode;
}
export function EditChartsModal({
  onClose,
  onSave,
  initialChartOrder,
  initialVisibleCharts,
  chartData,
  renderChart
}: EditChartsModalProps) {
  const {
    t
  } = useTranslation('analytics');
  const [chartOrder, setChartOrder] = useState<string[]>(initialChartOrder);
  const [visibleCharts, setVisibleCharts] = useState<Set<string>>(new Set(initialVisibleCharts));
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newOrder = [...chartOrder];
      const [draggedItem] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(dragOverIndex, 0, draggedItem);
      setChartOrder(newOrder);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  const handleRemoveChart = (chartId: string) => {
    const newVisible = new Set(visibleCharts);
    newVisible.delete(chartId);
    setVisibleCharts(newVisible);
  };
  const handleSave = () => {
    onSave(chartOrder, visibleCharts);
    onClose();
  };
  // Filter to only show visible charts
  const visibleChartOrder = chartOrder.filter(id => visibleCharts.has(id));
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-1819">
      <div role="dialog" aria-modal="true" aria-labelledby="edit-charts-modal-title" className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1820">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-1821">
          <div data-id="element-1822">
            <h2 id="edit-charts-modal-title" className="text-xl font-semibold text-gray-900" data-id="element-1823">
              {t('components.EditChartsModal.edit_charts')}
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1824">
              {t('components.EditChartsModal.drag_to_reorder_click_x_to')}
            </p>
          </div>
          <button onClick={onClose} aria-label={t('components.AddChartModal.cancel')} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-1825">
            <XIcon size={20} className="text-gray-500" aria-hidden="true" data-id="element-1826" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-1827">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1828">
            {visibleChartOrder.map((chartId, index) => {
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index;
            return <div key={chartId} draggable onDragStart={() => handleDragStart(index)} onDragOver={e => handleDragOver(e, index)} onDragEnd={handleDragEnd} className={`relative transition-all cursor-move ${isDragging ? 'opacity-50 scale-95' : isDragOver ? 'ring-2 ring-blue-500 rounded-lg' : ''}`} data-id="element-1829">
                  {/* Drag handle - positioned absolutely over the chart */}
                  <div className="absolute top-3 left-3 text-gray-400 bg-white rounded p-1 shadow-sm border border-gray-200 z-10" data-id="element-1830">
                    <GripVerticalIcon size={20} data-id="element-1831" />
                  </div>

                  {/* Remove button - positioned absolutely over the chart */}
                  <button onClick={() => handleRemoveChart(chartId)} aria-label="Remove chart" className="absolute top-3 right-3 p-1 bg-white hover:bg-gray-100 rounded-full transition-colors z-10 shadow-sm border border-gray-200" data-id="element-1832">
                    <XIcon size={16} className="text-gray-500" data-id="element-1833" />
                  </button>

                  {/* Actual chart component - non-interactive */}
                  <div className="pointer-events-none" data-id="element-1834">
                    {renderChart(chartId)}
                  </div>
                </div>;
          })}
          </div>

          {/* Empty state */}
          {visibleChartOrder.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-1835">
              <div className="text-gray-400 mb-2" data-id="element-1836">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-1837">
                  <rect x="3" y="3" width="18" height="18" rx="2" data-id="element-1838" />
                  <path d="M3 9h18" data-id="element-1839" />
                  <path d="M9 21V9" data-id="element-1840" />
                </svg>
              </div>
              <p className="text-sm text-gray-500" data-id="element-1841">
                {t('components.EditChartsModal.no_charts_to_display_add_charts')}
              </p>
            </div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200" data-id="element-1842">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1843">
            {t('components.AddChartModal.cancel')}
          </button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-1844">
            {t('components.EditChartsModal.done')}
          </button>
        </div>
      </div>
    </div>;
}